import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiCheck, FiCode, FiSliders } from 'react-icons/fi';
import { getArchitectureVariables, ArchitectureVariable } from '../../api/variables';

interface FieldWithSuggestionsProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suggestions?: string[];
  placeholder?: string;
  required?: boolean;
  description?: string;
  type?: 'text' | 'number';
  // Variable support props
  architectureId?: string;
  enableVariables?: boolean;
}

// Regex to detect variable references: {{variable_name}}
const VARIABLE_PATTERN = /\{\{([a-z][a-z0-9_]*)\}\}/g;
const VARIABLE_TRIGGER = '{{';

const FieldWithSuggestions: React.FC<FieldWithSuggestionsProps> = ({
  label,
  value,
  onChange,
  suggestions = [],
  placeholder,
  required = false,
  description,
  type = 'text',
  architectureId,
  enableVariables = true,
}) => {
  const { t } = useTranslation('canvas');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [variables, setVariables] = useState<ArchitectureVariable[]>([]);
  const [variableFilter, setVariableFilter] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Function to refresh variables
  const refreshVariables = useCallback(() => {
    if (architectureId && enableVariables) {
      getArchitectureVariables(architectureId)
        .then(setVariables)
        .catch(console.error);
    }
  }, [architectureId, enableVariables]);

  // Fetch architecture variables when architectureId changes
  useEffect(() => {
    refreshVariables();
  }, [refreshVariables]);

  // Refresh variables when input gains focus (to pick up newly created variables)
  const handleInputFocus = useCallback(() => {
    setInputFocused(true);
    refreshVariables(); // Refresh variables list when field is focused
  }, [refreshVariables]);

  // Update dropdown position when showing suggestions or when scrolling
  useEffect(() => {
    const updatePosition = () => {
      if ((showSuggestions || showVariablePicker) && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();

    if (showSuggestions || showVariablePicker) {
      // Use requestAnimationFrame for smoother updates
      let animationFrameId: number;
      const handleScrollOrResize = () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(updatePosition);
      };

      // Listen to window scroll, resize, and all parent scrolls
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);

      // Also listen to scroll events on all scrollable parent elements
      let parent = containerRef.current?.parentElement;
      const scrollableParents: HTMLElement[] = [];
      while (parent) {
        const overflow = window.getComputedStyle(parent).overflow;
        if (overflow === 'auto' || overflow === 'scroll') {
          scrollableParents.push(parent);
          parent.addEventListener('scroll', handleScrollOrResize);
        }
        parent = parent.parentElement;
      }

      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
        scrollableParents.forEach(p => p.removeEventListener('scroll', handleScrollOrResize));
      };
    }
  }, [showSuggestions, showVariablePicker]);

  // Check if the value contains variable references
  const containsVariables = useMemo(() => {
    return VARIABLE_PATTERN.test(value);
  }, [value]);

  // Extract variable names from the value
  const extractedVariables = useMemo(() => {
    const matches = value.matchAll(VARIABLE_PATTERN);
    return Array.from(matches, m => m[1]);
  }, [value]);

  // Check if we're currently typing a variable (cursor is after "{{")
  const checkVariableTrigger = useCallback((inputValue: string, position: number) => {
    const beforeCursor = inputValue.substring(0, position);
    const lastTriggerIndex = beforeCursor.lastIndexOf(VARIABLE_TRIGGER);

    if (lastTriggerIndex >= 0) {
      // Check if there's a closing }} between the trigger and cursor
      const afterTrigger = beforeCursor.substring(lastTriggerIndex + 2);
      if (!afterTrigger.includes('}}')) {
        // We're in the middle of typing a variable
        setVariableFilter(afterTrigger.toLowerCase());
        return true;
      }
    }
    return false;
  }, []);

  const handleSelectSuggestion = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  const handleSelectVariable = (variable: ArchitectureVariable) => {
    const beforeCursor = value.substring(0, cursorPosition);
    const afterCursor = value.substring(cursorPosition);

    // Find the last {{ trigger
    const lastTriggerIndex = beforeCursor.lastIndexOf(VARIABLE_TRIGGER);

    let newValue: string;
    let newCursorPos: number;

    if (lastTriggerIndex >= 0) {
      // Replace from {{ to cursor with the variable reference
      newValue = beforeCursor.substring(0, lastTriggerIndex) +
        `{{${variable.name}}}` +
        afterCursor;
      newCursorPos = lastTriggerIndex + variable.name.length + 4; // {{name}}
    } else {
      // No {{ found - direct insertion at cursor position
      newValue = beforeCursor + `{{${variable.name}}}` + afterCursor;
      newCursorPos = cursorPosition + variable.name.length + 4; // {{name}}
    }

    onChange(newValue);

    // Set cursor after the inserted variable
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        inputRef.current.focus();
      }
    }, 0);

    setShowVariablePicker(false);
    setVariableFilter('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const position = e.target.selectionStart || 0;
    setCursorPosition(position);
    onChange(newValue);

    // Check if we should show variable picker
    if (enableVariables && architectureId && variables.length > 0) {
      if (checkVariableTrigger(newValue, position)) {
        setShowVariablePicker(true);
        setShowSuggestions(false);
      } else {
        setShowVariablePicker(false);
      }
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const position = (e.target as HTMLInputElement).selectionStart || 0;
    setCursorPosition(position);

    // Check if we should show variable picker on cursor movement
    if (enableVariables && architectureId && variables.length > 0) {
      if (checkVariableTrigger(value, position)) {
        setShowVariablePicker(true);
        setShowSuggestions(false);
      } else {
        setShowVariablePicker(false);
      }
    }
  };

  // Open variable picker without inserting anything
  const handleInsertVariable = () => {
    if (inputRef.current) {
      // Just open the picker - don't insert anything
      setShowVariablePicker(true);
      setVariableFilter('');
      setShowSuggestions(false);

      // Keep focus on input
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const filteredSuggestions = suggestions.filter(s =>
    s.toLowerCase().includes(value.toLowerCase())
  );

  // Filter variables based on what the user is typing after {{
  const filteredVariables = useMemo(() => {
    if (!variableFilter) return variables;
    return variables.filter(v =>
      v.name.toLowerCase().includes(variableFilter) ||
      v.display_name?.toLowerCase().includes(variableFilter)
    );
  }, [variables, variableFilter]);

  // Always show dropdown UI for fields that can have suggestions (even if empty now)
  const canHaveSuggestions = suggestions !== undefined;
  const hasSuggestions = suggestions.length > 0;
  const hasVariables = enableVariables && architectureId && variables.length > 0;

  // Determine border color
  const getBorderColor = () => {
    if (containsVariables) return '#8B5CF6'; // Purple for variable references
    if (inputFocused) return '#3B82F6';
    return '#e5e7eb';
  };

  return (
    <div ref={containerRef} style={{ marginBottom: 12, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            {label}
            {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
          </label>
          {containsVariables && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10,
                padding: '2px 6px',
                background: '#F3E8FF',
                color: '#7C3AED',
                borderRadius: 4,
              }}
              title={t('fieldSuggestions.variableReference', 'Uses variable: {{vars}}').replace('{{vars}}', extractedVariables.join(', '))}
            >
              <FiCode size={10} />
              {extractedVariables.length === 1 ? extractedVariables[0] : `${extractedVariables.length} vars`}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {hasVariables && (
            <button
              onMouseDown={(e) => {
                // Prevent input blur when clicking the button
                e.preventDefault();
              }}
              onClick={handleInsertVariable}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                color: '#7C3AED',
              }}
              title={t('fieldSuggestions.insertVariable', 'Insert variable reference')}
            >
              <FiSliders size={14} />
            </button>
          )}
          {canHaveSuggestions && (
            <button
              onClick={() => setShowSuggestions(!showSuggestions)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                color: hasSuggestions ? '#6B7280' : '#D1D5DB',
              }}
              title={t('fieldSuggestions.showSuggestions')}
            >
              <FiChevronDown
                size={14}
                style={{
                  transform: showSuggestions ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
              <span>{t('fieldSuggestions.suggestionCount', { count: suggestions.length })}</span>
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={handleInputChange}
        onKeyUp={handleKeyUp}
        onFocus={() => {
          handleInputFocus(); // Refresh variables and set focused state
          if (canHaveSuggestions) {
            setShowSuggestions(true);
          }
        }}
        onBlur={() => {
          setInputFocused(false);
          // Delay closing to allow clicking on suggestions
          setTimeout(() => {
            setShowSuggestions(false);
            setShowVariablePicker(false);
          }, 200);
        }}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '8px 10px',
          border: `1px solid ${getBorderColor()}`,
          borderRadius: 8,
          fontSize: 12,
          outline: 'none',
          transition: 'border-color 0.2s',
          background: containsVariables ? '#FAFAFA' : 'white',
        }}
      />

      {description && (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
          {description}
        </div>
      )}

      {/* Variable Picker dropdown - Rendered in Portal to avoid clipping */}
      {showVariablePicker && filteredVariables.length > 0 && createPortal(
        <div
          onMouseDown={(e) => {
            // Prevent input blur when clicking in the dropdown
            e.preventDefault();
          }}
          style={{
            position: 'fixed',
            top: dropdownPosition.top + 4,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            background: 'white',
            border: '1px solid #C4B5FD',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.15)',
            zIndex: 10001,
            maxHeight: 250,
            overflowY: 'auto',
          }}
        >
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid #E5E7EB',
            fontSize: 11,
            color: '#7C3AED',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: '#F5F3FF',
          }}>
            <FiSliders size={12} />
            {t('fieldSuggestions.selectVariable', 'Select a variable')}
          </div>
          {filteredVariables.map((variable) => (
            <div
              key={variable.id}
              onClick={() => handleSelectVariable(variable)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#F5F3FF')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <code style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#7C3AED',
                    background: '#F3E8FF',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}>
                    {`{{${variable.name}}}`}
                  </code>
                  <span style={{
                    fontSize: 10,
                    padding: '2px 6px',
                    background: variable.scope === 'parameter' ? '#DBEAFE' : '#F3E8FF',
                    color: variable.scope === 'parameter' ? '#1D4ED8' : '#7C3AED',
                    borderRadius: 4,
                  }}>
                    {variable.scope}
                  </span>
                </div>
                {variable.display_name && (
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                    {variable.display_name}
                  </div>
                )}
              </div>
              {variable.type && (
                <span style={{
                  fontSize: 10,
                  color: '#9CA3AF',
                }}>
                  {variable.type}
                </span>
              )}
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* Regular Suggestions dropdown (hidden when variable picker is shown) - Rendered in Portal to avoid clipping */}
      {showSuggestions && !showVariablePicker && canHaveSuggestions && createPortal(
        <div
          onMouseDown={(e) => {
            // Prevent input blur when clicking in the dropdown
            e.preventDefault();
          }}
          style={{
            position: 'fixed',
            top: dropdownPosition.top + 4,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            background: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 10000,
            maxHeight: 200,
            overflowY: 'auto',
          }}
        >
          {filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={() => handleSelectSuggestion(suggestion)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: suggestion === value ? '#F3F4F6' : 'white',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = suggestion === value ? '#F3F4F6' : 'white')
                }
              >
                <span style={{ color: '#374151' }}>{suggestion}</span>
                {suggestion === value && (
                  <FiCheck size={14} style={{ color: '#3B82F6' }} />
                )}
              </div>
            ))
          ) : (
            <div style={{
              padding: '12px',
              fontSize: 12,
              color: '#9CA3AF',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              {t('fieldSuggestions.noResources', 'Aucune ressource disponible')}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default FieldWithSuggestions;
